import { PackagesAppView } from '../view/PackagesAppView';
import { templatePackages } from '../view/template';
import { BaseWebComponentController, IListenerConfig } from '../../../framework/BaseWebComponentController';
import { PackagesAppWebComponent } from '..';
import {
  DownloadButtonEvents,
  OnDownloadK8sAgent,
  OnDownloadSecurityContainer,
} from '../../downloadKitButtonSelector/events/events';
import { toggleVisibility } from '../../../utils/htmlElement.utils';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { OnClickPackage, PackagesGridEvents } from '../../packagesGrid/events/events';
import { PackagePanelWebComponent } from '../../packagePanel';
import { PackagePanelEvents } from '../../common/events/PackagePanelEvents';
import { PackageActionContext } from '../../common/types/PackageActionContext';
import { showCancelWarningDialog } from '../../../tagsManager/helpers/commonFunctions';
import { NOT_EDITABLE_TYPES } from '../constants';
import { GzDialog } from '../../../ui/gzDialog/component/GzDialog';
import { Feature } from '@nirvana/shared/constants';
import { PackagePanelType, PackageType } from '../../packagesGrid/types';
import { TagPanelWebComponent } from '../../../tagsManager/tagPanel';
import { translateFn } from '../../../utils/translator.helper';

export class PackagesAppController extends BaseWebComponentController {
  public component: PackagesAppWebComponent;
  public view: PackagesAppView;
  public viewConstructor: typeof PackagesAppView;
  private createPackageDialog: GzDialogWebComponent;
  private editPackageDialog: GzDialogWebComponent;

  constructor(component: PackagesAppWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: PackagesAppView,
      viewTemplate: templatePackages,
    });
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$packagesGrid,
        event: PackagesGridEvents.OnAddPackage,
        callback: this.showCreatePackage,
      },
      {
        element: this.view.dom.$packagesGrid,
        event: PackagesGridEvents.OnClickPackage,
        callback: this.showEditPackage,
      },
      {
        element: this.view.dom.$packagesGrid,
        event: DownloadButtonEvents.OnDownloadClicked,
        callback: () => {
          this.view.setLoadingState();
        },
      },
      {
        element: this.view.dom.$packagesGrid,
        event: DownloadButtonEvents.OnDownloadStarted,
        callback: () => {
          this.view.removeLoadingState();
        },
      },
      {
        element: this.view.dom.$packagesGrid,
        event: DownloadButtonEvents.OnDownloadSecurityContainer,
        callback: (event: OnDownloadSecurityContainer) => {
          this.showDownloadContainerWindow(event.detail.packageId).then().catch();
        },
      },
      {
        element: this.view.dom.$packagesGrid,
        event: DownloadButtonEvents.OnDownloadK8sAgent,
        callback: (event: OnDownloadK8sAgent) => {
          this.showDownloadK8sAgent().then().catch();
        },
      },
    ];
  }

  public async showCreatePackage(): Promise<void> {
    const createPackagePanel = new PackagePanelWebComponent();

    const dialogConfig = {
      headline: translateFn('Create Installation Package'),
      bodyContent: createPackagePanel,
      isOpen: true,
      isWindow: true,
      closableOutsideBox: false,
      onCloseHandler: showCancelWarningDialog.bind(this, this.closeCreatePackageWindow.bind(this)),
    };

    this.createPackageDialog = GzDialogWebComponent.buildDialogElement(dialogConfig, this.view.dom.$wrapper);
    createPackagePanel.formContext = PackageActionContext.Create;
    createPackagePanel.setPackageType(this.getPackageTypeByUserFeatures());
    createPackagePanel.panelType = PackagePanelType.INSTALLATION_PACKAGES;
    createPackagePanel.addEventListener(
      PackagePanelEvents.OnPackagePanelClose,
      this.closeCreatePackageWindow.bind(this)
    );
    createPackagePanel.addEventListener(
      PackagePanelEvents.OnPackagePanelMaybeClose,
      showCancelWarningDialog.bind(this, this.closeCreatePackageWindow.bind(this))
    );
    createPackagePanel.injectDependencies(this.dependencies);

    await createPackagePanel.start();
  }

  private async showDownloadContainerWindow(packageId: string): Promise<void> {
    this.view.setLoadingState();
    toggleVisibility(this.view.dom.$securityContainerWindow, false);

    //mocked to support backwards compatibilities, should be removed  when old version of packages is removed
    //todo remove this when old version of packages is removed
    this.dependencies.CloseDownloadSecurityContainerWindow = () => {};
    this.view.dom.$securityContainerWindow.injectDependencies(this.dependencies);
    this.view.dom.$securityContainerWindow.setPackageId(packageId);
    this.view.dom.$securityContainerWindow.setUseNewDataRepository(true);

    await Promise.all([this.view.dom.$securityContainerWindow.start()]);

    const footerContent = document.createElement('div');
    footerContent.innerHTML = `
        <gz-button id='button-cancel' type='outline' color='secondary'>
          ${translateFn('CLOSE')}
        </gz-button>
    `;
    const buttonCancel = footerContent.querySelector('#button-cancel');
    const dialogConfig = {
      headline: translateFn('Download Security Container'),
      bodyContent: this.view.dom.$securityContainerWindow,
      footerContent: footerContent,
      isOpen: true,
      isWindow: true,
      closableOutsideBox: false,
    };

    const dialog = GzDialogWebComponent.buildDialogElement(dialogConfig, this.view.dom.$wrapper);
    if (buttonCancel) {
      buttonCancel.addEventListener('click', () => {
        dialog.dismiss();
      });
    }
    toggleVisibility(this.view.dom.$securityContainerWindow, true);
    this.view.removeLoadingState();
    await this.view.dom.$securityContainerWindow.stop();
  }

  private async showDownloadK8sAgent(): Promise<void> {
    analytics.trackPageView({
      parentName: 'installationpackages',
      name: 'kubernetes installation',
    });

    this.view.setLoadingState();
    toggleVisibility(this.view.dom.$k8sAgentWindow, false);
    this.view.dom.$k8sAgentWindow.injectDependencies(this.dependencies);

    await this.view.dom.$k8sAgentWindow.start();

    const footerContent = document.createElement('div');
    footerContent.innerHTML = `
        <gz-button id='button-cancel' type='outline' color='secondary'>
          ${translateFn('CLOSE')}
        </gz-button>
    `;
    const buttonCancel = footerContent.querySelector('#button-cancel');
    const dialogConfig = {
      headline: translateFn('Download the Kubernetes agent'),
      bodyContent: this.view.dom.$k8sAgentWindow,
      footerContent: footerContent,
      isOpen: true,
      isWindow: true,
      closableOutsideBox: false,
    };

    const dialog = GzDialogWebComponent.buildDialogElement(dialogConfig, this.view.dom.$wrapper);
    if (buttonCancel) {
      buttonCancel.addEventListener('click', () => {
        dialog.dismiss();
      });
    }
    toggleVisibility(this.view.dom.$k8sAgentWindow, true);
    this.view.removeLoadingState();
    await this.view.dom.$k8sAgentWindow.stop();
  }

  public async showEditPackage(event: OnClickPackage): Promise<void> {
    const packageData = event.detail.packageData;
    if (NOT_EDITABLE_TYPES.indexOf(packageData.type) > -1) {
      return;
    }

    const editPackagePanel: PackagePanelWebComponent = new PackagePanelWebComponent();
    const onCloseHandler = this.maybeCloseEditPackagePanel.bind(this, editPackagePanel);
    const dialogConfig: GzDialog = {
      headline: translateFn(`Edit "{packageName}" installation package`, {
        packageName: packageData.name,
      }),
      bodyContent: editPackagePanel,
      isOpen: true,
      isWindow: true,
      closableOutsideBox: false,
      onCloseHandler: onCloseHandler,
    };

    this.editPackageDialog = GzDialogWebComponent.buildDialogElement(dialogConfig, this.view.dom.$wrapper);

    editPackagePanel.setPackageType(packageData.type);
    editPackagePanel.setPackageData(packageData);
    editPackagePanel.formContext = PackageActionContext.Edit;
    editPackagePanel.panelType = PackagePanelType.INSTALLATION_PACKAGES;
    editPackagePanel.addEventListener(PackagePanelEvents.OnPackagePanelClose, this.closeEditPackageWindow.bind(this));
    editPackagePanel.addEventListener(PackagePanelEvents.OnPackagePanelMaybeClose, onCloseHandler);
    editPackagePanel.injectDependencies(this.dependencies);

    await editPackagePanel.start();
  }

  private maybeCloseEditPackagePanel(editTagPanel: TagPanelWebComponent): void {
    if (editTagPanel.hasUnsavedChanges) {
      showCancelWarningDialog.call(this, () => {
        this.closeEditPackageWindow();
      });
    } else {
      this.closeEditPackageWindow();
    }
  }

  private closeCreatePackageWindow(): void {
    if (this.createPackageDialog === null) {
      return;
    }

    this.createPackageDialog.dismiss();
    this.view.dom.$packagesGrid.reload(true, false);
  }

  private closeEditPackageWindow(): void {
    if (this.editPackageDialog === null) {
      return;
    }

    this.editPackageDialog.dismiss();
    this.view.dom.$packagesGrid.reload(true, false);
  }

  private getPackageTypeByUserFeatures(): PackageType {
    return !this.dependencies.userHasFeature(Feature.PRODUCT_FEATURE_FRAT_VISIBLE)
      ? PackageType.Endpoints
      : PackageType.Frat;
  }
}
